import { JSX, useContext, useEffect, useRef, useState } from "react";
/* eslint-disable */
import { Page, PageInner } from "@app/shared";
import { QESAlert, QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import { Link, Redirect } from "react-router-dom";
import { BulkTargetingWizardContext, BulkTargetingWizardProvider } from "./import/TargetingWizardContext";
import { BulkTargetingApiResponseModel, BulkTargetingWizardSteps, BulkCreateProviderTargetingModel } from "./import/interfaces";
import TargetingWizardSelectProjects from "./import/TargetingWizardSelectProjects";
import TargetingWizardImportList from "./import/TargetingWizardImportList";
import TargetingWizardConfigureReport from "./import/TargetingWizardConfigureReport";
import TargetingWizardFlyout from "./import/TargetingWizardFlyout";
import { FlyoutLink } from "@app/shared/Flyouts";
import { commonFocusableElements, focusFirstInteractiveElement } from "@app/shared/utility";

const BulkTargetingWizard = (): JSX.Element => {
  return (
    <BulkTargetingWizardProvider>
      <BulkTargetingWizardContent />
    </BulkTargetingWizardProvider>
  );
};

const humanReadablePageNames = {
  [BulkTargetingWizardSteps.SelectProjects]: "Select Projects",
  [BulkTargetingWizardSteps.ImportList]: "Import List",
  [BulkTargetingWizardSteps.ConfigureReport]: "Configure Report",
};

const BulkTargetingWizardContent = (): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<BulkTargetingApiResponseModel[]>([]);
  const [redirectToView, setRedirectToView] = useState(false);
  const [preConflictNumberOfSelections, setPreConflictNumberOfSelections] = useState(0);

  useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, [wizardContext.page]);

  const attemptSubmit = async () => {
    const {
      bulkProviderTargetId,
      comment,
      emailOnSuccess,
      filename,
      formatType,
      selectedProjectIds,
      targetingReason,
      existingTargetAction,
      isDistinctServicingProviderReport
    } = wizardContext;

    if (
      !bulkProviderTargetId ||
      selectedProjectIds.length === 0 ||
      !filename ||
      !targetingReason ||
      (targetingReason === "Other" && !comment)
    ) {
      return false;
    }

    const model: BulkCreateProviderTargetingModel = {
      bulkTargetId: bulkProviderTargetId,
      customProjectIds: selectedProjectIds.map((id) => parseInt(id, 10)),
      emailOnSuccess,
      exportName: filename,
      formatType,
      reasonDescription: comment,
      reasonName: targetingReason,
      existingTargetAction,
      isDistinctServicingProviderReport
    };

    await wizardContext.projectionProviderService.bulkCreateProviderTargeting(model);
    setRedirectToView(true);
    return true;
  };

  const getConfirmationSidePanelContent = () => {
    return <TargetingWizardFlyout onSubmit={attemptSubmit} targetAction={wizardContext.existingTargetAction} />;
  };

  const validateAndAdvance = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.SelectProjects:
        if (wizardContext.selectedProjectIds.length > 0) {
          wizardContext.setReviewProjects(
            wizardContext.projects.filter((p) => wizardContext.selectedProjectIds.includes(p.projectId.toString())));
          wizardContext.setPage(BulkTargetingWizardSteps.ImportList);
        }
        break;
      case BulkTargetingWizardSteps.ImportList:
        if (!!wizardContext.bulkImportFormData) {
          wizardContext.setPage(BulkTargetingWizardSteps.ConfigureReport);
        }
        break;
    } // The other possible value, .ConfigureReport, has a different button at the bottom
  };

  const renderCurrentWizardStep = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.SelectProjects:
        return <TargetingWizardSelectProjects stepIsValid={setPageIsValid} />;
      case BulkTargetingWizardSteps.ImportList:
        return <TargetingWizardImportList stepIsValid={setPageIsValid} />;
      case BulkTargetingWizardSteps.ConfigureReport:
        return <TargetingWizardConfigureReport stepIsValid={setPageIsValid} />;
    }
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.ImportList:
        wizardContext.setPage(BulkTargetingWizardSteps.SelectProjects);
        break;
      case BulkTargetingWizardSteps.ConfigureReport:
        wizardContext.setPage(BulkTargetingWizardSteps.ImportList);
        break;
    }
  };

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(BulkTargetingWizardSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as BulkTargetingWizardSteps;
    // "as any" because TS complains about using a number to index an enum and complains about using a string to index an object
    wizardContext.setPage(BulkTargetingWizardSteps[step] as any);
    setConflicts([]);
    setPreConflictNumberOfSelections(wizardContext.selectedProjectIds.length);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [wizardContext.page]);

  useEffect(() => {
    // if we have selected projects, hit api endpoint for fetching projects
    wizardContext.projectionProviderService.getBulkTargetingProjectsAsync().then((response) => {
      // make sure no selected projects have changed into readonly mode
      const newConflicts: BulkTargetingApiResponseModel[] = [];
      const newSelectedProjectIds: string[] = [];
      response.forEach((r) => {
        if (wizardContext.selectedProjectIds.includes(r.projectId.toString()) && r.isReadOnly) {
          newConflicts.push(r);
        } else if (wizardContext.selectedProjectIds.includes(r.projectId.toString())) {
          newSelectedProjectIds.push(r.projectId.toString());
        }
      });
      // if they have, set the page back to 1 and trigger an alert banner to let the user know why they're back here, that their progress was saved, and what projects were effected
      if (newConflicts.length > 0 && newConflicts.length === wizardContext.selectedProjectIds.length) {
        wizardContext.setPage(BulkTargetingWizardSteps.SelectProjects);
      }
      // pulling this into a variable because I don't know how React batches set calls, and don't want to use a state value that's changing in another set call
      const oldProjectsSelected = wizardContext.selectedProjectIds.length;
      setPreConflictNumberOfSelections(oldProjectsSelected);
      if (newConflicts.length > 0) {
        wizardContext.setSelectedProjectIds(newSelectedProjectIds);
      }

      setConflicts(newConflicts);
    });
  }, [wizardContext.page]);

  if (redirectToView) {
    return <Redirect to="/targeting/view" push={false} />;
  }

  return (
    <Page documentTitle="New Volume Targeting">
      {/* The design for the wizard does not have breadcrumbs */}
      <PageInner className="targeting-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">New Targets</h1>
        </header>
        <WizardSteps currentStep={wizardContext.page} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />
        {conflicts.length > 0 && conflicts.length < preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info" collapseTitle="The following selected projects have been changed to read only mode and unselected:">
            <ul className="mb-0">
              {conflicts.map((conflict) => <li key={conflict.projectId}>{conflict.projectName}</li>)}
            </ul>
          </QESAlert>
        )}
        {conflicts.length > 0 && conflicts.length === preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info">
            All of the projects you selected have been changed to read only mode. No other selections made during the volume targeting setup process have been lost.
          </QESAlert>
        )}
        <hr />
        <div ref={wizardBodyRef}>
          {renderCurrentWizardStep()}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/targeting/view" className="qes-button qes-button-secondary danger" data-testid="import-targeting__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="new-terms__nav-buttons">
            {wizardContext.page !== BulkTargetingWizardSteps.SelectProjects && <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="import-targeting__previous">Previous</QESButton>}
            {wizardContext.page === BulkTargetingWizardSteps.ConfigureReport ? (
              <FlyoutLink
                disabled={!pageIsValid}
                content={{
                  body: getConfirmationSidePanelContent(),
                  title: "Warning",
                }}
                classNames="qes-button-primary ml-3"
                testId="import-targeting-submit"
                size="40"
              >
                Submit
              </FlyoutLink>
            ) : (
              <QESButton onClick={validateAndAdvance} disabled={!pageIsValid} qesTestId="import-targeting__next" className="ml-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default BulkTargetingWizard;